import { useCallback, useMemo, useRef, useState } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { RoleMappingAccount } from "../roles/requiredAccountsMapping";
import { createTempId } from "./tempId";
import {
    AllocateCodeText,
    DraftAccount,
    DraftGroup,
    DraftState,
    EditableAccountPatch,
    MoveRowArgs,
    MoveRowsArgs,
    addAccountInScope as addAccountInScopeOp,
    addGroupInScope as addGroupInScopeOp,
    createGroup as createGroupOp,
    deleteAccount as deleteAccountOp,
    deleteGroup as deleteGroupOp,
    isStateDirty,
    moveRow as moveRowOp,
    moveRows as moveRowsOp,
    renameGroup as renameGroupOp,
    ScopeInsertPosition,
    seedDraftState,
    selectDraftStateAfterSave,
    serializeDraftState,
    setAccountDisabled as setAccountDisabledOp,
    updateAccount as updateAccountOp,
} from "./draftOperations";
import { applyRoleAssignments as applyRoleAssignmentsOp } from "./templatesHost/applyRoleAssignments";

export interface TemplateEditorDraft {
    accounts: DraftState["accounts"];
    groups: DraftState["groups"];
    isDirty: boolean;
    updateAccount: (id: string, patch: EditableAccountPatch) => void;
    addAccountInScope: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        position?: ScopeInsertPosition;
        codeText: string | null;
    }) => string;
    // Computes the create suggestion once per gesture against the synchronously
    // maintained working copy (same source as createGroup / captureSignature),
    // so the fan-out callers write the same code onto every business's new row.
    suggestCreateCodeText: (params: {
        type: AccountType;
        targets: Array<{ entityId: number; parentGroupId: string | null }>;
    }) => string | null;
    deleteAccount: (id: string) => void;
    applyRoleAssignments: (
        workingAccounts: readonly RoleMappingAccount[],
    ) => void;
    replaceState: (state: DraftState) => void;
    setAccountDisabled: (id: string, isDisabled: boolean) => void;
    moveRow: (args: MoveRowArgs) => void;
    moveRows: (args: MoveRowsArgs) => void;
    createGroup: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        name: string;
        position?: ScopeInsertPosition;
    }) => string;
    // Inline empty-named group create for the nested per-row "Add group"
    // affordance; never dedupes (validation surfaces blank/duplicate names).
    addGroupInScope: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        position?: ScopeInsertPosition;
    }) => string;
    renameGroup: (id: string, name: string) => void;
    deleteGroup: (id: string) => void;
    captureSignature: () => string;
    resetToServerIfUnchanged: (params: {
        nextState: DraftState;
        snapshotSignature: string;
    }) => void;
}

interface DraftStore {
    state: DraftState;
    baselineSignature: string;
}

const makeStore = (seed: {
    accounts: DraftAccount[];
    groups: DraftGroup[];
}): DraftStore => {
    const state = seedDraftState(seed);
    return { state, baselineSignature: serializeDraftState(state) };
};

interface UseTemplateEditorDraftParams {
    // The host-mapped initial draft (server rows projected onto DraftAccount /
    // DraftGroup). Read once to seed; later changes are ignored (the drawer
    // remounts the host per template).
    accounts: DraftAccount[];
    groups: DraftGroup[];
    // Injected code allocation. Templates scan the type band; a live-chart host
    // returns the current value on a type change and null on create.
    allocateCodeText: AllocateCodeText;
}

export const useTemplateEditorDraft = ({
    accounts,
    groups,
    allocateCodeText,
}: UseTemplateEditorDraftParams): TemplateEditorDraft => {
    const [store, setStore] = useState<DraftStore>(() =>
        makeStore({ accounts, groups }),
    );

    // Read the injected allocator at call time so the operation callbacks stay
    // stable even if the host re-derives it each render.
    const allocateCodeTextRef = useRef(allocateCodeText);
    allocateCodeTextRef.current = allocateCodeText;

    // Working copy of the draft, kept current synchronously so createGroup can
    // dedupe and return the canonical group id within a single tick — before
    // React processes the setStore updater and re-renders. Each render resyncs
    // it to the committed state.
    const stateRef = useRef(store.state);
    stateRef.current = store.state;

    const updateAccount = useCallback(
        (id: string, patch: EditableAccountPatch) =>
            setStore((prev) => ({
                ...prev,
                state: updateAccountOp({ state: prev.state, id, patch }),
            })),
        [],
    );

    const addAccountInScope = useCallback(
        ({
            entityId,
            type,
            parentGroupId,
            position,
            codeText,
        }: {
            entityId: number;
            type: AccountType;
            parentGroupId: string | null;
            position?: ScopeInsertPosition;
            codeText: string | null;
        }): string => {
            const accountId = createTempId();
            setStore((prev) => ({
                ...prev,
                state: addAccountInScopeOp({
                    state: prev.state,
                    entityId,
                    type,
                    parentGroupId,
                    position,
                    generateId: () => accountId,
                    codeText,
                }).state,
            }));
            return accountId;
        },
        [],
    );

    const suggestCreateCodeText = useCallback(
        ({
            type,
            targets,
        }: {
            type: AccountType;
            targets: Array<{ entityId: number; parentGroupId: string | null }>;
        }): string | null =>
            allocateCodeTextRef.current({
                kind: "create",
                type,
                accounts: stateRef.current.accounts,
                targets,
            }),
        [],
    );

    const deleteAccount = useCallback(
        (id: string) =>
            setStore((prev) => ({
                ...prev,
                state: deleteAccountOp({ state: prev.state, id }),
            })),
        [],
    );

    const applyRoleAssignments = useCallback(
        (workingAccounts: readonly RoleMappingAccount[]) =>
            setStore((prev) => ({
                ...prev,
                state: applyRoleAssignmentsOp({
                    state: prev.state,
                    workingAccounts,
                    generateId: createTempId,
                }),
            })),
        [],
    );

    const replaceState = useCallback(
        (state: DraftState) => setStore((prev) => ({ ...prev, state })),
        [],
    );

    const setAccountDisabled = useCallback(
        (id: string, isDisabled: boolean) =>
            setStore((prev) => ({
                ...prev,
                state: setAccountDisabledOp({
                    state: prev.state,
                    id,
                    isDisabled,
                }),
            })),
        [],
    );

    const moveRow = useCallback(
        (args: MoveRowArgs) =>
            setStore((prev) => ({
                ...prev,
                state: moveRowOp({
                    state: prev.state,
                    allocateCodeText: allocateCodeTextRef.current,
                    ...args,
                }),
            })),
        [],
    );

    const moveRows = useCallback(
        (args: MoveRowsArgs) =>
            setStore((prev) => ({
                ...prev,
                state: moveRowsOp({
                    state: prev.state,
                    allocateCodeText: allocateCodeTextRef.current,
                    ...args,
                }),
            })),
        [],
    );

    const createGroup = useCallback(
        ({
            entityId,
            type,
            parentGroupId,
            name,
            position,
        }: {
            entityId: number;
            type: AccountType;
            parentGroupId: string | null;
            name: string;
            position?: ScopeInsertPosition;
        }): string => {
            // Resolve the dedupe against the synchronously-maintained working
            // state (not the possibly-stale committed state): createGroupOp
            // returns the existing group's id when a case-insensitive name match
            // in scope already exists, otherwise the freshly-minted id. This
            // guarantees the returned id always references a group that ends up
            // in the resulting state — even for two rapid creates in one tick.
            const { state: nextState, groupId } = createGroupOp({
                state: stateRef.current,
                entityId,
                type,
                parentGroupId,
                name,
                position,
                generateId: createTempId,
            });
            const didCreate = nextState !== stateRef.current;
            if (didCreate) {
                // Advance the working ref now so a second create in the same
                // tick dedupes against this not-yet-committed group instead of
                // minting a phantom id the updater would discard. The committed
                // update still threads off `prev` to avoid clobbering other
                // queued edits.
                stateRef.current = nextState;
                setStore((prev) => ({
                    ...prev,
                    state: createGroupOp({
                        state: prev.state,
                        entityId,
                        type,
                        parentGroupId,
                        name,
                        position,
                        generateId: () => groupId,
                    }).state,
                }));
            }
            return groupId;
        },
        [],
    );

    // Inline empty-named group create (nested per-row "Add group"). No dedupe is
    // needed, so — unlike createGroup — this doesn't have to thread the working
    // ref: the freshly-minted id always ends up in the resulting state.
    const addGroupInScope = useCallback(
        ({
            entityId,
            type,
            parentGroupId,
            position,
        }: {
            entityId: number;
            type: AccountType;
            parentGroupId: string | null;
            position?: ScopeInsertPosition;
        }): string => {
            const groupId = createTempId();
            setStore((prev) => ({
                ...prev,
                state: addGroupInScopeOp({
                    state: prev.state,
                    entityId,
                    type,
                    parentGroupId,
                    position,
                    generateId: () => groupId,
                }).state,
            }));
            return groupId;
        },
        [],
    );

    const renameGroup = useCallback(
        (id: string, name: string) =>
            setStore((prev) => ({
                ...prev,
                state: renameGroupOp({ state: prev.state, id, name }),
            })),
        [],
    );

    const deleteGroup = useCallback(
        (id: string) =>
            setStore((prev) => ({
                ...prev,
                state: deleteGroupOp({ state: prev.state, id }),
            })),
        [],
    );

    // Snapshot of the draft taken when a save is initiated. Read from the
    // synchronously-maintained working ref so it reflects every edit committed
    // up to the click, not a stale closure.
    const captureSignature = useCallback(
        () => serializeDraftState(stateRef.current),
        [],
    );

    // Cells stay editable while a save is in flight. On success we only adopt
    // the server response when the draft is still identical to the snapshot
    // captured at save time; if the user edited meanwhile, their draft is kept
    // (and stays dirty) so in-flight edits are never silently discarded. The
    // host maps the server response into a DraftState before handing it here.
    const resetToServerIfUnchanged = useCallback(
        ({
            nextState,
            snapshotSignature,
        }: {
            nextState: DraftState;
            snapshotSignature: string;
        }) =>
            setStore((prev) => {
                // Re-seed the mapped server state so its array order matches a
                // fresh load (sorted), mirroring the original makeStore path.
                const serverStore = makeStore(nextState);
                const nextSelected = selectDraftStateAfterSave({
                    currentState: prev.state,
                    serverState: serverStore.state,
                    snapshotSignature,
                });
                return nextSelected === serverStore.state ? serverStore : prev;
            }),
        [],
    );

    const isDirty = useMemo(
        () =>
            isStateDirty({
                state: store.state,
                baselineSignature: store.baselineSignature,
            }),
        [store],
    );

    return {
        accounts: store.state.accounts,
        groups: store.state.groups,
        isDirty,
        updateAccount,
        addAccountInScope,
        suggestCreateCodeText,
        deleteAccount,
        applyRoleAssignments,
        replaceState,
        setAccountDisabled,
        moveRow,
        moveRows,
        createGroup,
        addGroupInScope,
        renameGroup,
        deleteGroup,
        captureSignature,
        resetToServerIfUnchanged,
    };
};
